import { takeEvery, delay } from 'redux-saga';
import { fork, put, call } from 'redux-saga/effects';
import { push } from 'react-router-redux';

import {
  VERIFY_USER_REQUEST,
  VERIFY_USER_FAILURE,
  VERIFY_USER_SUCCESS
} from 'constants/actionTypes';
import { verificationErrors } from 'constants/errors';
import { emailVerification } from 'constants/statuses';
import { verifyUserFailure, verifyUserSuccess } from 'actions/verificationActions';
import { verifyEmailRequest } from 'requesters/verificationRequester';
import { loggedInCheck } from 'sagas/loginSaga';
import { redirectUser } from 'sagas/redirectionSaga';

/**
 * Verification API call.
 * On success, dispatch verifyUserSuccess and redirects to success screen
 * On failure, dispatch verifyUserFailure and redirects to failure screen
 * @param {Object} action
 * @param {String} action.verificationToken
 * @returns {void}
 */
export function *verifyUser({verificationToken}) {
  if (!verificationToken) {
    return yield put(verifyUserFailure('verificationFailed.genericError'));
  }

  const login = yield call(loggedInCheck);

  if (login) {
    try {
      yield call(verifyEmailRequest, verificationToken);
      yield put(verifyUserSuccess({
        ...login.user,
        emailVerificationStatus: emailVerification.VERIFIED
      }));
      return yield call(redirectUser);

    } catch (error) {
      const errorId = error.extraData ? error.extraData['error-code'] : 'generic-error';

      return yield put(verifyUserFailure(verificationErrors[errorId]));
    }
  } else {
    return yield put(push(`/login`));
  }
}

/**
 * Verification failed.
 * Redirects user to correct screen
 * @param {Object} action
 * @param {String} action.reason
 * @return {void}
 */
export function *verificationFailed() {
  yield put(push(`/verification/failed`));
}

/**
 * Verification succeed.
 * Redirects user to correct screen
 * @param {Object} action
 * @param {Object} action.user
 * @return {void}
 */
export function *verificationSucceed() {
  yield put(push(`/verification/success`));
  yield call(delay, 3000);
  yield call(redirectUser);
}

/**
 * Triggers user verification
 * @return {void}
 */
export function *verifyUserWatcher() {
  yield *takeEvery(VERIFY_USER_REQUEST, verifyUser);
}

/**
 * Show verification failed screen
 * @return {void}
 */
export function *verifyUserFailureWatcher() {
  yield *takeEvery(VERIFY_USER_FAILURE, verificationFailed);
}

/**
 * Show verification success screen
 * @return {void}
 */
export function *verifyUserSuccessWatcher() {
  yield *takeEvery(VERIFY_USER_SUCCESS, verificationSucceed);
}

/**
 * Uses fork to start multiple watchers in parallel
 * @return {void}
 */
export default function *rootSaga() {
  yield fork(verifyUserWatcher);
  yield fork(verifyUserFailureWatcher);
  yield fork(verifyUserSuccessWatcher);
}
